import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import React, {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";

import {
  AutoCompleteUncontrolled,
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
} from "@4uhub/lib4uhub";

interface IMacroFilterProps {
  onFilterParams: Dispatch<SetStateAction<IFilterParams>>;
  filterParams: IFilterParams;
}

export interface IFilterParams {
  identifier: string;
  pacientName: string;
  documentCompanion: string;
  visitorIndividualName: string;
  statusSend: string;
  CreatedStart: Date | null;
  CreatedEnd?: Date | null;
  refresh?: boolean;
  orderBySlowest?: boolean;
}

const translationPath = "page.4uBus.logs.gerAcessoVisitControlSector.";

const MacroFilter: React.FC<IMacroFilterProps> = ({
  onFilterParams,
  filterParams,
}) => {
  const { filter, showCleanBtn, initialState, setFilter, cleanFilter } =
    useFilter(filterParams);

  const { t } = useTranslation();

  const filterHandler = () => {
    onFilterParams(filter);
  };

  const [resetDateRange, setResetDateRange] = useState<boolean>(false);

  const cleanHandler = () => {
    onFilterParams(initialState.current);
    cleanFilter();
    setResetDateRange((prev) => !prev);
  };

  const [enableFields, setEnableFields] = useState<boolean>(true);

  const handleStartDate = (date: Date | null) => {
    setFilter({ CreatedStart: date });
  };

  const handleEndDate = (date: Date | null) => {
    setFilter({ CreatedEnd: date });
  };

  const [selectedStatus, setSelectedStatus] = useState<ISelectType | null>(
    null,
  );

  const handleSelectStatus = useCallback((value: ISelectType | null) => {
    setFilter({ statusSend: value?.id ?? "" });
    setSelectedStatus(value);
  }, []);

  const statuses: ISelectType[] = useMemo(() => {
    return [
      {
        id: "PENDENTE",
        name: t(translationPath + "status.pending"),
        code: "P",
      },
      {
        id: "ENVIANDO",
        name: t(translationPath + "status.sending"),
        code: "EN",
      },
      {
        id: "PROCESSANDO",
        name: t(translationPath + "status.processing"),
        code: "PR",
      },
      {
        id: "FINALIZADO",
        name: t(translationPath + "status.finished"),
        code: "F",
      },
      {
        id: "ERRO",
        name: t(translationPath + "status.error"),
        code: "E",
      },
    ];
  }, [t]);

  return (
    <Stack flexDirection={"row"} gap={2} alignItems="center">
      <DateRangeSelect
        setEndDate={handleEndDate}
        setStartDate={handleStartDate}
        setEnableFields={setEnableFields}
        resetToToday={resetDateRange}
      />

      <Box sx={{ flex: "0 0 160px" }}>
        <DatePickerUncontrolled
          value={filter.CreatedStart}
          onChange={(e) => setFilter({ CreatedStart: e })}
          label={`${t(translationPath + "start_date")}`}
          isClearable={false}
          disable={enableFields}
        />
      </Box>

      <Box sx={{ flex: "0 0 160px" }}>
        <DatePickerUncontrolled
          value={filter.CreatedEnd}
          onChange={(e) => setFilter({ CreatedEnd: e })}
          label={`${t(translationPath + "end_date")}`}
          isClearable={false}
          disable={enableFields}
        />
      </Box>

      <TextField
        value={filter.identifier}
        size="small"
        label={t(translationPath + "identifier")}
        fullWidth
        onChange={(e) =>
          setFilter({ identifier: e.target.value.replace(/\D/g, "") })
        }
      />

      <TextField
        value={filter.pacientName}
        size="small"
        label={t(translationPath + "patient_name")}
        fullWidth
        onChange={(e) => setFilter({ pacientName: e.target.value })}
      />

      <TextField
        value={filter.documentCompanion}
        size="small"
        label={t(translationPath + "visitor_cpf")}
        fullWidth
        onChange={(e) => setFilter({ documentCompanion: e.target.value })}
      />

      <TextField
        value={filter.visitorIndividualName}
        size="small"
        label={t(translationPath + "visitor")}
        fullWidth
        onChange={(e) => setFilter({ visitorIndividualName: e.target.value })}
      />

      <AutoCompleteUncontrolled
        label={t(translationPath + "status_item") || "Status"}
        onValueSelected={handleSelectStatus}
        value={selectedStatus}
        options={statuses}
        size="small"
        fullWidth
        sx={{ flex: "0 0 150px", minWidth: 150 }}
      />

      <Tooltip title={t(translationPath + "order_by_slowest_info")}>
        <FormControlLabel
          sx={{ whiteSpace: "nowrap", marginRight: 0 }}
          control={
            <Switch
              size="small"
              checked={!!filter.orderBySlowest}
              onChange={(e) => setFilter({ orderBySlowest: e.target.checked })}
            />
          }
          label={t(translationPath + "order_by_slowest")}
        />
      </Tooltip>

      <FiltersActions
        onFilter={filterHandler}
        onClean={cleanHandler}
        showCleanBtn={showCleanBtn}
      />
    </Stack>
  );
};

export default memo(MacroFilter);
